import { useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { EntityFormDialog } from "@/components/common/EntityFormDialog";
import { ErrorState, LoadingState, Pill, ScoreBar } from "@/components/common/States";
import { Timeline } from "@/components/crm/Timeline";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch, useLookups } from "@/lib/hooks";
import { CLASSIFICATIONS, SCORES, STAGES, labelOf, opportunities } from "@/lib/resources";
import { fmtDate, fmtMoney } from "@/lib/api";

function Detail({ label, children }) {
  return <div><p className="eyebrow mb-1">{label}</p><div className="text-sm">{children || <span className="text-muted-foreground">—</span>}</div></div>;
}

function LinkedEvidence({ opportunityId }) {
  const { data } = useFetch("/evidence", { opportunity_id: opportunityId });
  const claimsRes = useFetch("/claims", { opportunity_id: opportunityId });
  return (
    <div data-testid="opportunity-evidence">
      <p className="eyebrow mb-3">Linked evidence & claims</p>
      {!data?.length && !claimsRes.data?.length && <p className="text-sm text-muted-foreground">No evidence linked yet. Link evidence records from Research → Evidence.</p>}
      <div className="space-y-2">
        {data?.map((e) => (
          <div key={e.id} className="rounded-md border p-3 text-xs">
            <div className="mb-1 flex gap-2"><Pill value={e.verification_status === "verified" ? "active" : e.verification_status === "rejected" ? "rejected" : "medium"}>{e.verification_status}</Pill><span className="text-muted-foreground">{e.evidence_type.replace(/_/g, " ")}</span></div>
            <p className="italic">“{e.excerpt}”</p>
          </div>
        ))}
        {claimsRes.data?.map((c) => (
          <div key={c.id} className="flex items-start gap-2 rounded-md border p-3 text-xs"><Pill value={c.status === "verified" || c.status === "corroborated" ? "active" : c.status === "contradicted" ? "rejected" : "medium"}>{c.status}</Pill><span>{c.statement}</span></div>
        ))}
      </div>
    </div>
  );
}

export function OpportunityPanel({ id, onClose, onChanged }) {
  const { api, can } = useWorkspace();
  const { data: o, loading, error, reload } = useFetch(id ? `/opportunities/${id}` : null);
  const lookups = useLookups(opportunities.lookups);
  const [editing, setEditing] = useState(false);
  const [tlKey, setTlKey] = useState(0);

  const save = async (payload) => {
    await api.patch(`/opportunities/${id}`, payload);
    toast.success("Opportunity updated");
    reload(); onChanged?.(); setTlKey((k) => k + 1);
  };

  return (
    <Sheet open={Boolean(id)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="opportunity-panel">
        {loading && !o && <LoadingState />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {o && o.id === id && (
          <div className="space-y-6">
            <SheetHeader className="space-y-3 text-left">
              <div className="flex flex-wrap gap-2"><Pill value={o.stage}>{labelOf(STAGES, o.stage)}</Pill><Pill value={o.classification} testid="opportunity-classification">{labelOf(CLASSIFICATIONS, o.classification)}</Pill></div>
              <SheetTitle className="font-display text-2xl font-extrabold tracking-tight" data-testid="opportunity-panel-title">{o.name}</SheetTitle>
              <Link to={`/companies/${o.company_id}`} className="text-sm text-primary hover:underline" data-testid="opportunity-company-link">{lookups.companies?.[o.company_id] || "View company"}</Link>
            </SheetHeader>
            {can("write") && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="opportunity-edit-button"><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>}
            <div className="grid grid-cols-2 gap-4">
              <Detail label="Value"><span className="font-mono">{fmtMoney(o.value, o.currency)}</span></Detail>
              <Detail label="Expected close">{o.expected_close_date && fmtDate(o.expected_close_date)}</Detail>
              <Detail label="Product">{lookups.products?.[o.product_profile_id]}</Detail>
              <Detail label="Site">{lookups.sites?.[o.site_id]}</Detail>
              <Detail label="Primary contact">{lookups.contacts?.[o.primary_contact_id]}</Detail>
              <Detail label="Owner">{lookups.members?.[o.owner_user_id]}</Detail>
            </div>
            <div className="panel space-y-3 p-4" data-testid="opportunity-scores">
              {SCORES.map(([k, l]) => <ScoreBar key={k} label={l} value={o[k]} testid={`score-${k}`} />)}
              <p className="text-[11px] text-muted-foreground">Scores are entered manually in Milestone 1. Evidence-based scoring arrives with the research engine.</p>
            </div>
            <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground">
              <ShieldAlert className="h-4 w-4 shrink-0 text-amber-500" />
              Confirmed Procurement can only be set when a verified evidence record of a relevant purchasing requirement is linked.
            </div>
            {o.summary && <Detail label="Summary"><p className="whitespace-pre-wrap">{o.summary}</p></Detail>}
            {o.next_step && <Detail label="Next step">{o.next_step}</Detail>}
            <LinkedEvidence opportunityId={id} />
            <div><p className="eyebrow mb-3">Activity timeline</p><Timeline key={tlKey} activityParams={{ opportunity_id: id }} entityId={id} link={{ opportunity_id: id, company_id: o.company_id }} testid="opportunity-timeline" /></div>
          </div>
        )}
        <EntityFormDialog open={editing} onOpenChange={setEditing} title="Edit opportunity" fields={opportunities.fields} record={o} lookups={lookups} onSubmit={save} testid="opportunity-form" />
      </SheetContent>
    </Sheet>
  );
}
